import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, MessageCircle } from "lucide-react";
import { Overline } from "../components/SectionHeading";
import { AuthShell, authInput, authLabel, authPrimaryBtn, authSecondaryBtn, formatDetail } from "../components/auth/AuthShell";
import { WhatsAppOtpPanel } from "../components/auth/WhatsAppOtpPanel";
import { useAuth } from "../context/AuthContext";
import { LOGIN } from "../constants/testIds/auth";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [waPhone, setWaPhone] = useState("");
  const [mode, setMode] = useState("password");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const u = await login(identifier.trim(), password);
      const next = new URLSearchParams(window.location.search).get("next");
      navigate(next && next.startsWith("/") ? next : u?.role === "admin" ? "/admin" : "/");
    } catch (err) {
      setError(err?.response ? formatDetail(err?.response?.data?.detail) : "We couldn't reach the server. Please check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      testId="login-page"
      image="/brand/heena-hero-desktop.jpg"
      imageAlt="Heena, the HESHAURA muse, wearing a handcrafted bracelet"
      imagePosition="object-[42%_22%]"
      quote="“Bracelets for a brighter you.”"
      caption="Welcome back to HESHAURA"
    >
      <Overline>Account</Overline>
      <h1 className="mt-4 font-serif text-4xl font-medium tracking-tight text-brand-charcoal sm:text-5xl">Welcome back.</h1>
      <p className="mt-3 text-sm text-brand-stone">Sign in to your HESHAURA account.</p>

      <AnimatePresence mode="wait" initial={false}>
        {mode === "password" ? (
          <motion.form
            key="password"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.35 }}
            onSubmit={onSubmit}
            className="mt-8 space-y-5"
          >
            <div>
              <label htmlFor="login-identifier" className={authLabel}>
                Email or WhatsApp Number
              </label>
              <input
                id="login-identifier"
                type="text"
                required
                autoComplete="username"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="you@example.com or 98xxxxxxxx"
                data-testid={LOGIN.emailInput}
                className={authInput}
              />
            </div>
            <div>
              <label htmlFor="login-password" className={authLabel}>
                Password
              </label>
              <input
                id="login-password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid={LOGIN.passwordInput}
                className={authInput}
              />
            </div>
            {error && (
              <p className="text-sm font-medium text-destructive" data-testid="login-error">
                {error}
              </p>
            )}
            <button type="submit" disabled={busy} data-testid={LOGIN.submitButton} className={authPrimaryBtn}>
              {busy ? "Signing in…" : "Sign In"}
            </button>
            <div className="flex items-center gap-3 py-1 text-[10px] uppercase tracking-[0.25em] text-brand-stone">
              <span className="h-px flex-1 bg-brand-sand" /> or <span className="h-px flex-1 bg-brand-sand" />
            </div>
            <button type="button" onClick={() => setMode("whatsapp")} data-testid={LOGIN.whatsappToggle} className={authSecondaryBtn}>
              <MessageCircle className="h-4 w-4" /> Login with WhatsApp Code
            </button>
          </motion.form>
        ) : (
          <motion.div
            key="whatsapp"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.35 }}
            className="mt-8 space-y-5"
          >
            <WhatsAppOtpPanel phone={waPhone} onPhoneChange={setWaPhone} onVerified={() => navigate("/")} />
            <button
              type="button"
              onClick={() => setMode("password")}
              data-testid={LOGIN.backToPassword}
              className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-stone transition-colors hover:text-brand-gold"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to password login
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <p className="mt-8 text-center text-sm text-brand-stone">
        New to HESHAURA?{" "}
        <Link to="/register" data-testid={LOGIN.registerLink} className="font-semibold text-brand-gold underline-offset-4 hover:underline">
          Create an account
        </Link>
      </p>
    </AuthShell>
  );
}
